import { useEffect, useRef } from "react";
import { useAuth, useUser } from "@clerk/react";
import { useSubscription } from "@clerk/react/experimental";
import { resolveTierFromClerk, isClerkBillingSyncEnabled, isClerkConfigured } from "@/lib/clerkEntitlements";
import { applyTierCookies, clearSessionEmail, clearSessionUserId, clearTierCookies, setSessionUserId } from "@/lib/userSession";

const clerkConfigured = isClerkConfigured();

/**
 * Keeps access_level / plan cookies in sync with Clerk Billing (Beta).
 * Opt-in: VITE_CLERK_BILLING_ENABLED=true
 */
export function useClerkEntitlementSync() {
  const enabled = clerkConfigured && isClerkBillingSyncEnabled();
  const { isLoaded, isSignedIn, has, userId } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const { data: subscription, isLoading, isFetching, error } = useSubscription({
    enabled: enabled && isLoaded && Boolean(isSignedIn),
    for: "user",
  });

  const hasRef = useRef(has);
  hasRef.current = has;

  useEffect(() => {
    if (!enabled) return;
    if (!isLoaded) return;

    if (!isSignedIn) {
      clearTierCookies();
      clearSessionEmail();
      clearSessionUserId();
      try {
        if (typeof window !== "undefined") {
          window.localStorage.removeItem("user_email");
          window.localStorage.removeItem("user_id");
        }
      } catch {
        /* ignore */
      }
      return;
    }

    if (!userLoaded) return;
    if (isLoading || isFetching) return;

    const email = user?.primaryEmailAddress?.emailAddress ?? null;
    const tier = resolveTierFromClerk(hasRef.current, error ? null : subscription);
    setSessionUserId(userId || user?.id || null);
    try {
      if (typeof window !== "undefined") {
        const resolvedUserId = userId || user?.id || null;
        if (resolvedUserId) {
          window.localStorage.setItem("user_id", resolvedUserId);
        } else {
          window.localStorage.removeItem("user_id");
        }
      }
    } catch {
      /* ignore */
    }

    if (tier) {
      applyTierCookies({ accessLevel: "pro", plan: tier, email });
    } else {
      applyTierCookies({ accessLevel: "free", plan: null, email });
    }
  }, [
    enabled,
    isLoaded,
    isSignedIn,
    userLoaded,
    user,
    subscription,
    isLoading,
    isFetching,
    error,
    userId,
  ]);
}
